import AudienceTemplateModalType from '@/Enums/AudienceTemplates/ModalType.enum';
import type { IBaseAudienceTargeting } from '@/Interfaces/Audiences/Audience.interface';
import type { IAudienceControlOption } from '@/Interfaces/ControlOption.interface';
import AudienceTargetingService from '@/Services/AudienceTargeting.service';
import AudienceHeader from '@/components/AudienceHeader';
import AudienceListContainer from '@/components/AudienceList';
import AudienceTemplatesModal from '@/components/AudienceTemplates/AudienceTemplatesModal';
import SupportRequestModal from '@/components/Modal/SupportRequestModal';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectExtendedAudiences } from '@/store/selectors/currentWhitelabel.selectors';
import { selectDV360TicketsAudiences } from '@/store/selectors/dv360Tickets/insertionOrders.selectors';
import { selectIsAdmin } from '@/store/selectors/me.selectors';
import { getDV360TicketsAudiences } from '@/store/slices/dv360Tickets/insertionOrderManagement.slice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

const DV360TicketsAudienceSection = () => {
  const dispatch = useAppDispatch();
  const { setValue, control } = useFormContext();
  const extendedAudiences = useAppSelector(selectExtendedAudiences);
  const isAdmin = useAppSelector(selectIsAdmin);
  const { audiences, loading } = useAppSelector(selectDV360TicketsAudiences);

  const watchAudiences = useWatch({ control, name: 'audiences' });

  const [isRequestAudiencesModalOpen, setIsRequestAudiencesModalOpen] = useState<boolean>(false);
  const [templatesModal, setTemplatesModal] = useState<{ open: boolean; type: AudienceTemplateModalType }>({
    open: false,
    type: AudienceTemplateModalType.View,
  });

  const openTemplates = useCallback((type: AudienceTemplateModalType) => setTemplatesModal({ open: true, type }), []);

  const closeTemplates = useCallback(() => setTemplatesModal(state => ({ ...state, open: false })), []);

  const currentAudiences = useMemo(
    () => watchAudiences ?? AudienceTargetingService.defaultAudienceTargeting,
    [watchAudiences],
  );

  const onAudienceChange = useCallback(
    (selectedAudiences: IBaseAudienceTargeting<IAudienceControlOption<number>>) => {
      setValue('audiences', selectedAudiences, { shouldDirty: true });
    },
    [setValue],
  );

  const onResetAudiences = useCallback(() => {
    onAudienceChange(AudienceTargetingService.defaultAudienceTargeting);
  }, [onAudienceChange]);

  const onRequestAudiences = useCallback(() => {
    setIsRequestAudiencesModalOpen(true);
  }, []);

  useEffect(() => {
    if (!audiences?.length && !loading) {
      dispatch(getDV360TicketsAudiences());
    }
  }, [audiences, dispatch, loading]);

  return (
    <div className='flex flex-col w-full'>
      <AudienceHeader
        onResetAudience={onResetAudiences}
        onRequestAudiences={onRequestAudiences}
        templatesEnabled
        onCreateTemplate={isAdmin ? () => openTemplates(AudienceTemplateModalType.Create) : undefined}
        onViewTemplates={() => openTemplates(AudienceTemplateModalType.View)}
      />
      <AudienceListContainer
        allowExtendedGrouping={extendedAudiences}
        onChange={onAudienceChange}
        options={audiences}
        loading={loading}
        containerSx={{ paddingTop: '15px' }}
        value={currentAudiences}
        isModeDisabled
      />
      <SupportRequestModal
        title='Audiences'
        isOpen={isRequestAudiencesModalOpen}
        handleClose={() => setIsRequestAudiencesModalOpen(false)}
      />
      <AudienceTemplatesModal
        open={templatesModal.open}
        initialType={templatesModal.type}
        options={audiences}
        currentValue={currentAudiences}
        allowExtendedGrouping={extendedAudiences}
        onClose={closeTemplates}
        onApply={onAudienceChange}
      />
    </div>
  );
};

export default DV360TicketsAudienceSection;
